<template>
  <div
    class="screen-recording-display"
    :class="[aspectRatioClass, { 'is-fullscreen': isFullscreen, 'is-playing': isPlaying, 'is-recording': isRecording }]"
    :style="{ backgroundColor: dynamicBackgroundColor }"
    @mousemove="handleMouseMove"
    @click="togglePlay"
    tabindex="0"
  >
    <!-- Recording countdown overlay -->
    <recording-countdown
      ref="countdown"
      :duration="countdownDuration"
      @complete="onCountdownComplete"
      @cancelled="onCountdownCancelled"
    />

    <!-- Recording Error Notification -->
    <transition name="fade">
      <div v-if="recordingError" class="recording-error-notification">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ recordingError }}</span>
        <button @click="recordingError = null" class="error-close-btn">&times;</button>
      </div>
    </transition>

    <div v-if="documents.length > 0" class="documents-stack">
      <!-- Loop through all documents and display them -->
      <div
        v-for="(document, index) in documents"
        :key="index"
        class="document-item"
      >
        <div class="document-container">
          <!-- Document Preview -->
          <div class="document-preview">
            <!-- Show video if it's an MP4/MOV file -->
            <div class="media-container video-container" v-if="isVideoFile(document.type) && (document.preview || document.dataUrl)">
              <div class="media-content">
                <video
                  ref="videoElement"
                  autoplay
                  loop
                  muted
                  class="document-video"
                  :src="document.preview || document.dataUrl"
                  :alt="document.name"
                  @error="handleMediaError"
                ></video>
              </div>

              <!-- Add Live Transcript component with font settings -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
                :font-family="fontSettings.fontFamily"
                :font-size="fontSettings.fontSize"
                :font-weight="fontSettings.fontWeight"
                :line-height="fontSettings.lineHeight"
                :letter-spacing="fontSettings.letterSpacing"
                :text-color="fontSettings.textColor"
                :text-shadow="fontSettings.textShadow"
              />
            </div>

            <!-- If it's a video but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isVideoFile(document.type)" class="media-container video-placeholder">
              <div class="media-content">
                <div class="placeholder-icon">🎥</div>
                <div class="video-name">{{ document.name }}</div>
                <div class="video-info">Video preview not available</div>
              </div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
                :font-family="fontSettings.fontFamily"
                :font-size="fontSettings.fontSize"
                :font-weight="fontSettings.fontWeight"
                :line-height="fontSettings.lineHeight"
                :letter-spacing="fontSettings.letterSpacing"
                :text-color="fontSettings.textColor"
                :text-shadow="fontSettings.textShadow"
              />
            </div>

            <!-- Show image with animation if it's an image and we have preview data -->
            <div class="media-container image-container" v-else-if="isImageFile(document.type) && (document.preview || document.dataUrl)">
              <div class="media-content">
                <div class="image-animation-container" :class="[currentAnimation, noRepeatAnimation ? 'no-repeat' : '']">
                  <img
                    :src="document.preview || document.dataUrl"
                    :alt="document.name"
                    class="document-image"
                    @error="handleMediaError"
                  />
                </div>
              </div>

              <!-- Add Live Transcript component -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
                :font-family="fontSettings.fontFamily"
                :font-size="fontSettings.fontSize"
                :font-weight="fontSettings.fontWeight"
                :line-height="fontSettings.lineHeight"
                :letter-spacing="fontSettings.letterSpacing"
                :text-color="fontSettings.textColor"
                :text-shadow="fontSettings.textShadow"
              />
            </div>

            <!-- If it's an image but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isImageFile(document.type)" class="media-container image-placeholder">
              <div class="media-content">
                <div class="placeholder-icon">🖼️</div>
                <div class="image-name">{{ document.name }}</div>
                <div class="image-info">Image preview not available</div>
              </div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
                :font-family="fontSettings.fontFamily"
                :font-size="fontSettings.fontSize"
                :font-weight="fontSettings.fontWeight"
                :line-height="fontSettings.lineHeight"
                :letter-spacing="fontSettings.letterSpacing"
                :text-color="fontSettings.textColor"
                :text-shadow="fontSettings.textShadow"
              />
            </div>

            <!-- Non-image, non-video document (PDF, DOCX, etc) -->
            <div v-else class="media-container file-icon-container">
              <div class="media-content">
                <div class="file-icon" :class="getFileIconClass(document.type)">
                  {{ getFileExtension(document.name).toUpperCase() }}
                </div>
                <div class="document-name">{{ document.name }}</div>
              </div>

              <!-- Add Live Transcript component for document files -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
                :font-family="fontSettings.fontFamily"
                :font-size="fontSettings.fontSize"
                :font-weight="fontSettings.fontWeight"
                :line-height="fontSettings.lineHeight"
                :letter-spacing="fontSettings.letterSpacing"
                :text-color="fontSettings.textColor"
                :text-shadow="fontSettings.textShadow"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- UI Controls Layer - Only visible before playing or when manually shown -->
    <div class="ui-controls-layer" :class="{ 'hidden': (isPlaying && !showControls && recordingMode) || forceHideAllUI, 'recording-hidden': isRecording }">
      <!-- Large play/record buttons (only visible when paused) -->
      <div v-if="!isPlaying && !isPreparingRecording" class="large-play-button-container">
        <button class="nav-button prev-button" @click.stop="skipPrevious" aria-label="Previous segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="19 20 9 12 19 4 19 20"></polygon>
            <line x1="5" y1="19" x2="5" y2="5"></line>
          </svg>
        </button>

        <!-- Play button -->
        <button class="large-play-button" @click.stop="playOnly" aria-label="Play">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
        </button>

        <!-- Record button (if recording mode is enabled) -->
        <button v-if="recordingMode" class="large-record-button" @click.stop="recordAndPlay" aria-label="Record and Play">
          <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="6" fill="currentColor"></circle>
          </svg>
        </button>

        <button class="nav-button next-button" @click.stop="skipNext" aria-label="Next segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 4 15 12 5 20 5 4"></polygon>
            <line x1="19" y1="5" x2="19" y2="19"></line>
          </svg>
        </button>
      </div>

      <!-- Minimalist fullscreen toggle - Special design for screen recording -->
      <button
        v-if="!isRecording"
        @click.stop="toggleFullscreen"
        class="fullscreen-toggle"
        :class="{ 'recording-mode': recordingMode }"
        aria-label="Toggle fullscreen"
      >
        <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
          <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
          <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
          <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
          <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
          <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
          <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
        </svg>
      </button>

      <!-- Only show aspect ratio and animation options when not playing -->
      <div v-if="!isPlaying && !isRecording && (isFullscreen || isMobile)" class="recording-settings">
        <!-- Settings toggle button -->
        <button @click.stop="toggleSettings" class="settings-toggle" aria-label="Toggle settings">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>

        <!-- Settings panel (conditionally shown) -->
        <div v-if="showSettingsPanel" class="settings-panel" @click.stop>
          <!-- Recording mode toggle -->
          <div class="settings-section">
            <label class="settings-label">
              <input type="checkbox" v-model="recordingMode" @change="saveSettings">
              <span>Recording Mode</span>
              <span class="settings-hint">(Enables tab recording with screen capture)</span>
            </label>
          </div>

          <div class="background-color-section">
            <div class="customization-label">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>
              <span>Background Color</span>
            </div>
            <div class="color-picker-container">
              <div class="current-color" :style="{ backgroundColor: podcastBackgroundColor }"></div>
              <input
                type="color"
                v-model="podcastBackgroundColor"
                class="color-picker"
                @change="handlePodcastBackgroundColorChange"
              />
              <button class="reset-button" @click="resetPodcastBackgroundColor">Reset to Default</button>
            </div>
          </div>

          <!-- Font Settings Section -->
          <div class="font-settings-section">
            <div class="customization-label">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="4 7 4 4 20 4 20 7"></polyline>
                <line x1="9" y1="20" x2="15" y2="20"></line>
                <line x1="12" y1="4" x2="12" y2="20"></line>
              </svg>
              <span>Font Settings</span>
            </div>

            <!-- Font Family -->
            <div class="font-control">
              <label>Font Family:</label>
              <select v-model="fontSettings.fontFamily" @change="saveFontSettings" class="font-select">
                <option value="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">System Default</option>
                <option value="'Helvetica Neue', Helvetica, Arial, sans-serif">Helvetica</option>
                <option value="'Times New Roman', Times, serif">Times New Roman</option>
                <option value="'Georgia', serif">Georgia</option>
                <option value="'Arial', sans-serif">Arial</option>
                <option value="'Verdana', sans-serif">Verdana</option>
                <option value="'Inter', sans-serif">Inter</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Lato', sans-serif">Lato</option>
              </select>
            </div>

            <!-- Font Size -->
            <div class="font-control">
              <label>Font Size:</label>
              <div class="range-control">
                <input
                  type="range"
                  v-model="fontSettings.fontSize"
                  @input="saveFontSettings"
                  min="12"
                  max="32"
                  step="1"
                  class="font-range"
                />
                <span class="range-value">{{ fontSettings.fontSize }}px</span>
              </div>
            </div>

            <!-- Font Weight -->
            <div class="font-control">
              <label>Font Weight:</label>
              <select v-model="fontSettings.fontWeight" @change="saveFontSettings" class="font-select">
                <option value="300">Light (300)</option>
                <option value="400">Normal (400)</option>
                <option value="500">Medium (500)</option>
                <option value="600">Semi Bold (600)</option>
                <option value="700">Bold (700)</option>
                <option value="800">Extra Bold (800)</option>
              </select>
            </div>

            <!-- Line Height -->
            <div class="font-control">
              <label>Line Height:</label>
              <div class="range-control">
                <input
                  type="range"
                  v-model="fontSettings.lineHeight"
                  @input="saveFontSettings"
                  min="1.0"
                  max="2.5"
                  step="0.1"
                  class="font-range"
                />
                <span class="range-value">{{ fontSettings.lineHeight }}</span>
              </div>
            </div>

            <!-- Letter Spacing -->
            <div class="font-control">
              <label>Letter Spacing:</label>
              <div class="range-control">
                <input
                  type="range"
                  v-model="fontSettings.letterSpacing"
                  @input="saveFontSettings"
                  min="-1"
                  max="3"
                  step="0.1"
                  class="font-range"
                />
                <span class="range-value">{{ fontSettings.letterSpacing }}px</span>
              </div>
            </div>

            <!-- Text Color -->
            <div class="font-control">
              <label>Text Color:</label>
              <div class="color-picker-container">
                <div class="current-color" :style="{ backgroundColor: fontSettings.textColor }"></div>
                <input
                  type="color"
                  v-model="fontSettings.textColor"
                  class="color-picker"
                  @change="saveFontSettings"
                />
                <button class="reset-button" @click="resetFontSettings">Reset Fonts</button>
              </div>
            </div>

            <!-- Font Presets -->
            <div class="font-control">
              <label>Font Presets:</label>
              <div class="setting-buttons">
                <button
                  v-for="preset in fontPresets"
                  :key="preset.name"
                  @click.stop="applyFontPreset(preset.name)"
                  class="setting-button"
                  :class="{ 'active': currentFontPreset === preset.name }"
                >
                  {{ preset.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Aspect Ratio -->
          <div class="settings-section">
            <label>Aspect Ratio:</label>
            <div class="setting-buttons">
              <button
                v-for="ratio in availableAspectRatios"
                :key="ratio.value"
                @click.stop="setAspectRatio(ratio.value)"
                :class="{ active: aspectRatio === ratio.value }"
                class="setting-button"
              >
                {{ ratio.label }}
              </button>
            </div>
          </div>

          <!-- Animation style selector (only for images) -->
          <div v-if="hasImages" class="settings-section">
            <label>Animation Style:</label>
            <div class="setting-buttons">
              <button
                v-for="anim in availableAnimations"
                :key="anim.value"
                @click.stop="setAnimationStyle(anim.value)"
                :class="{ active: animationStyle === anim.value }"
                class="setting-button"
              >
                {{ anim.label }}
              </button>
            </div>

            <!-- Animation options -->
            <div class="animation-options">
              <label class="settings-checkbox">
                <input type="checkbox" v-model="holdAnimationAtEnd" @change="saveSettings">
                <span>Hold at end position</span>
              </label>

              <label class="settings-checkbox">
                <input type="checkbox" v-model="synchronizeWithSpeaking" @change="saveSettings">
                <span>Match speaking duration</span>
              </label>

              <label v-if="!holdAnimationAtEnd && !synchronizeWithSpeaking" class="settings-checkbox">
                <input type="checkbox" v-model="noRepeatAnimation" @change="saveSettings">
                <span>Don't repeat animation</span>
              </label>
            </div>
          </div>
          <div class="settings-section">
            <label>Animation Control:</label>

            <label class="settings-checkbox">
              <input type="checkbox" v-model="animateOnlyWhenPlaying" @change="saveSettings">
              <span>Only animate when playing</span>
            </label>

            <label class="settings-checkbox">
              <input type="checkbox" v-model="disableAnimation" @change="saveSettings">
              <span>Disable all animations</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Keyboard shortcut overlay - Press '?' to toggle -->
    <div v-if="showKeyboardShortcuts" class="keyboard-shortcuts-overlay" @click.stop="showKeyboardShortcuts = false">
      <div class="keyboard-shortcuts-panel" @click.stop>
        <h3>Keyboard Shortcuts</h3>
        <div class="shortcut-list">
          <div class="shortcut-item">
            <span class="key">Space</span>
            <span>Play/Pause</span>
          </div>
          <div class="shortcut-item">
            <span class="key">F</span>
            <span>Toggle Fullscreen</span>
          </div>
          <div class="shortcut-item">
            <span class="key">←</span>
            <span>Previous Segment</span>
          </div>
          <div class="shortcut-item">
            <span class="key">→</span>
            <span>Next Segment</span>
          </div>
          <div class="shortcut-item">
            <span class="key">R</span>
            <span>Toggle Recording Mode</span>
          </div>
          <div class="shortcut-item" v-if="isRecording">
            <span class="key">Q</span>
            <span>Stop Recording</span>
          </div>
          <div class="shortcut-item" v-if="isRecording">
            <span class="key">P</span>
            <span>Pause/Resume Recording</span>
          </div>
          <div class="shortcut-item">
            <span class="key">?</span>
            <span>Show/Hide Shortcuts</span>
          </div>
        </div>
        <button @click.stop="showKeyboardShortcuts = false" class="close-button">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import LiveTranscript from '../components/LiveTranscript.vue';
import tabRecordingMixin from '../mixins/tabRecordingMixin';
import RecordingCountdown from '../components/recording/RecordingCountdown.vue';

export default {
  name: 'ScreenRecordingDisplay',
  components: {
    LiveTranscript,
    RecordingCountdown
  },
  mixins: [tabRecordingMixin],
  props: {
    conversationEnded: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true,
    },
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
    },
    documents: {
      type: Array,
      default: () => []
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    isLastSegment: {
      type: Boolean,
      default: false
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    },
    transcriptBackgroundColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.6)'
    },
    isMobile: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      animateOnlyWhenPlaying: false,  // New setting
      disableAnimation: false,     // Disable animations by default
      enableEndFade: true,
      fadeDuration: 2,
      showEndFade: false,
      fadeActive: false,
      fadeTimeout: null,

      // UI Controls
      podcastBackgroundColor: 'rgba(0, 0, 0, 0.1)',
      showControls: false,
      controlsTimer: null,
      showSettingsPanel: false,
      recordingMode: true,
      showKeyboardShortcuts: false,

      // Font Settings
      fontSettings: {
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 18,
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 0.3,
        textColor: 'white',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
      },

      // Font Presets
      fontPresets: [
        {
          name: 'default',
          label: 'Default',
          settings: {
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: 0.3,
            textColor: 'white',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          }
        },
        {
          name: 'large',
          label: 'Large',
          settings: {
            fontFamily: 'Arial, sans-serif',
            fontSize: 24,
            fontWeight: 500,
            lineHeight: 1.6,
            letterSpacing: 0.5,
            textColor: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)'
          }
        },
        {
          name: 'elegant',
          label: 'Elegant',
          settings: {
            fontFamily: 'Georgia, serif',
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1.7,
            letterSpacing: 0.2,
            textColor: '#f8f8f8',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
          }
        },
        {
          name: 'bold',
          label: 'Bold',
          settings: {
            fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.4,
            letterSpacing: 0.4,
            textColor: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.6)'
          }
        },
        {
          name: 'minimal',
          label: 'Minimal',
          settings: {
            fontFamily: 'Inter, sans-serif',
            fontSize: 16,
            fontWeight: 300,
            lineHeight: 1.8,
            letterSpacing: 0.1,
            textColor: '#f0f0f0',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
          }
        }
      ],

      currentFontPreset: 'default',

      // Animation options
      availableAnimations: [
        { value: 'random', label: 'Random' },
        { value: 'zoom-pan', label: 'Zoom & Pan' },
        { value: 'ken-burns', label: 'Ken Burns' },
        { value: 'fade-scale', label: 'Fade & Scale' },
        { value: 'gentle-float', label: 'Gentle Float' },
        { value: 'smooth-slide', label: 'Smooth Slide' },
        { value: 'hollywood-dramatic', label: 'Hollywood Dramatic' },
        { value: 'epic-reveal', label: 'Epic Reveal' },
        { value: 'aerial-sweep', label: 'Aerial Sweep' },
        { value: 'directors-cut', label: 'Director\'s Cut' },
        { value: 'epic-pan', label: 'Epic Pan' },
        { value: 'dolly-zoom', label: 'Dolly Zoom' },
      ],
      availableAspectRatios: [
        { value: '16:9', label: '16:9' },
        { value: '4:3', label: '4:3' },
        { value: '1:1', label: '1:1' },
        { value: '9:16', label: '9:16' }
      ],
      animations: [
        'zoom-pan-effect',
        'ken-burns-effect',
        'fade-and-scale',
        'gentle-float',
        'smooth-slide',
        'hollywood-dramatic',
        'epic-reveal',
        'dramatic-focus',
        'aerial-sweep',
        'directors-cut',
        'epic-pan',
        'dolly-zoom',
      ],
      animationIndex: 0,
      animationStyle: 'dolly-zoom',
      aspectRatio: '16:9',
      isFullscreen: false,
      animationTimer: null,
      baseAnimation: 'dolly-zoom',
      noRepeatAnimation: false,
      animationDuration: 30,
      animationStartTime: 0,
      previousSegmentContent: '',
      holdAnimationAtEnd: true,
      synchronizeWithSpeaking: true,
      segmentInProgress: false,

      // Transcript settings
      transcriptOpacity: 0.7,

      // Last interaction timestamp for inactivity detection
      lastInteractionTime: Date.now(),

      // Recording pause state
      isRecordingPaused: false,

      // Force hide all UI during recording
      forceHideAllUI: false,

      pendingStream: null,

      // Countdown states
      countdownDuration: 7,
      showCountdown: false
    };
  },
  computed: {
    currentAnimation() {
      // If animations are disabled, return empty string
      if (this.disableAnimation) {
        return '';
      }

      // If set to animate only when playing and not currently playing, return empty
      if (this.animateOnlyWhenPlaying && !this.isPlaying) {
        return '';
      }

      // Otherwise return the current animation
      return this.baseAnimation;
    },
    dynamicBackgroundColor() {
      return this.podcastBackgroundColor || 'rgba(0, 0, 0, 0.1)';
    },
    // Determine if there are images to animate
    hasImages() {
      return this.documents.some(doc => this.isImageFile(doc.type));
    },
    // Get current aspect ratio class for CSS
    aspectRatioClass() {
      switch (this.aspectRatio) {
        case '16:9': return 'aspect-16-9';
        case '4:3': return 'aspect-4-3';
        case '1:1': return 'aspect-1-1';
        case '9:16': return 'aspect-9-16';
        default: return 'aspect-16-9';
      }
    }
  },
  watch: {
    conversationEnded(newVal) {
      // stop recording if conversation ended
      setTimeout(() => {
        if (newVal && this.isRecording) {
          this.stopRecording();
        }
      }, 1000);
    },
    // ADD THIS WATCHER to sync prop changes with data:
    podcastBackgroundColor: {
      handler(newColor) {
        if (newColor && newColor !== this.podcastBackgroundColor) {
          this.podcastBackgroundColor = newColor;
        }
      },
      immediate: true
    },
    // Watch for transcript background color and opacity changes
    transcriptOpacity: {
      handler() {
        this.updateTranscriptBackground();
      }
    },
    // Watch for isPlaying changes to update UI
    isPlaying(newVal) {
      if (newVal) {
        if (this.animateOnlyWhenPlaying) {
          this.restartAnimations();
        }
        // Started playing
        this.segmentInProgress = true;
        this.restartAnimations();

        // Always hide controls when playing in fullscreen
        if (this.isFullscreen) {
          this.showControls = false;
          this.forceHideAllUI = true; // Add this line
        } else if (this.recordingMode && this.isRecording) {
          this.showControls = false;
        } else {
          this.showControlsTemporarily();
        }
      } else {
        // Stopped playing
        this.segmentInProgress = false;
        this.forceHideAllUI = false; // Add this line

        // Always show controls when stopped, unless recording
        if (!this.isRecording) {
          this.showControls = true;
        }
      }
    },
    disableAnimation(newVal) {
      if (newVal) {
        // Stop all animations
        const animContainer = this.$el.querySelector('.image-animation-container');
        if (animContainer) {
          this.animations.forEach(anim => {
            animContainer.classList.remove(anim);
          });
        }
      } else {
        // Restart animations
        this.restartAnimations();
      }
    },

    animateOnlyWhenPlaying(newVal) {
      // Restart animations when this setting changes
      this.restartAnimations();
    },
    // Watch for segment content changes to handle animations
    activeSegmentContent(newContent, oldContent) {
      // Reset fade when content changes
      if (newContent !== oldContent && newContent !== '') {
        // Save previous content for comparison
        this.previousSegmentContent = oldContent;
        this.segmentInProgress = true;

        // Handle animation style changes
        if (this.animationStyle === 'random' && this.isPlaying) {
          this.changeRandomAnimation();
        }

        // Handle animation timing
        if (this.synchronizeWithSpeaking && this.isPlaying) {
          this.restartAnimations();
          this.animationStartTime = Date.now();
        }
      } else if (newContent === '' && oldContent !== '') {
        this.segmentInProgress = false;
      }
    },
    // Watch recording mode changes
    recordingMode(newVal) {
      // If entering recording mode, hide controls
      if (newVal && this.isPlaying) {
        this.showControls = false;
      }
    }
  },
  mounted() {
    this.loadSettings();
    this.loadFontSettings();
    this.startAnimationCycle();
    this.addEventListeners();
    this.updateTranscriptBackground();
  },
  beforeDestroy() {
    this.removeEventListeners();

    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }

    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }
  },
  methods: {
    // Font Settings Methods
    saveFontSettings() {
      // Save font settings to localStorage
      localStorage.setItem(`font_settings_${this.messageId}`, JSON.stringify(this.fontSettings));

      // Determine current preset
      this.currentFontPreset = this.getCurrentFontPreset();
    },

    loadFontSettings() {
      const savedFontSettings = localStorage.getItem(`font_settings_${this.messageId}`);
      if (savedFontSettings) {
        try {
          const settings = JSON.parse(savedFontSettings);
          this.fontSettings = { ...this.fontSettings, ...settings };
          this.currentFontPreset = this.getCurrentFontPreset();
          console.log('Font settings loaded:', this.fontSettings);
        } catch (e) {
          console.error('Error loading font settings:', e);
        }
      }
    },

    getCurrentFontPreset() {
      // Check if current settings match any preset
      for (const preset of this.fontPresets) {
        const match = Object.keys(preset.settings).every(key => {
          return String(this.fontSettings[key]) === String(preset.settings[key]);
        });
        if (match) {
          return preset.name;
        }
      }
      return 'custom';
    },

    applyFontPreset(presetName) {
      const preset = this.fontPresets.find(p => p.name === presetName);
      if (preset) {
        this.fontSettings = { ...preset.settings };
        this.currentFontPreset = presetName;
        this.saveFontSettings();
      }
    },

    resetFontSettings() {
      this.fontSettings = {
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 18,
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 0.3,
        textColor: 'white',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
      };
      this.currentFontPreset = 'default';
      this.saveFontSettings();
    },

    // Method to start the fade transition
    // ---- Event Handling ----
    handlePodcastBackgroundColorChange() {
      this.saveSettings();
    },
    resetPodcastBackgroundColor() {
      this.podcastBackgroundColor = 'rgba(0, 0, 0, 0.1)';
      this.saveSettings();
    },
    addEventListeners() {
      // Add fullscreen change event listener
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

      // Add keyboard event listeners
      window.addEventListener('keydown', this.handleKeyDown);

      // Mouse movement tracking
      this.$el.addEventListener('mousemove', this.handleMouseMove);
    },

    removeEventListeners() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      window.removeEventListener('keydown', this.handleKeyDown);
      this.$el.removeEventListener('mousemove', this.handleMouseMove);
    },

    handleKeyDown(e) {
      // Only handle if this component is focused
      if (document.activeElement !== this.$el) return;

      // Handle recording-specific keys first
      if (this.isRecording) {
        this.handleRecordingKeyDown(e);
        return;
      }

      switch (e.key) {
        case ' ': // Space - Play/Pause
          e.preventDefault();
          this.togglePlay();
          break;
        case 'f': // F - Fullscreen
        case 'F':
          e.preventDefault();
          this.toggleFullscreen();
          break;
        case 'ArrowLeft': // Left arrow - Previous
          e.preventDefault();
          this.skipPrevious();
          break;
        case 'ArrowRight': // Right arrow - Next
          e.preventDefault();
          this.skipNext();
          break;
        case '?': // ? - Show keyboard shortcuts
          e.preventDefault();
          this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
          break;
        case 'r': // R - Toggle recording mode
        case 'R':
          e.preventDefault();
          this.recordingMode = !this.recordingMode;
          this.saveSettings();
          break;
      }
    },

    handleEscKey() {
      if (this.isRecording) {
        this.stopRecording();
      } else if (this.isFullscreen) {
        this.exitFullscreen();
      }
    },

    handleMouseMove() {
      // Update last interaction time
      this.lastInteractionTime = Date.now();

      // Only show controls on mouse move if not in fullscreen playing mode
      if (!(this.isFullscreen && this.isPlaying)) {
        if (!this.recordingMode || !this.isPlaying || !this.isRecording) {
          this.showControlsTemporarily();
        }
      }
    },

    // Handle play start event
    handlePlayStart() {
      this.segmentInProgress = true;
      this.animationStartTime = Date.now();
      this.restartAnimations();

      // Hide controls after short delay if in recording mode
      if (this.recordingMode && this.isRecording) {
        this.showControls = false;
      }
    },

    // Handle play stop event
    handlePlayStop() {
      this.segmentInProgress = false;

      // Always show controls when stopped, unless recording
      if (!this.isRecording) {
        this.showControls = true;
      }
    },

    // ---- UI Control Methods ----
    togglePlay() {
      this.$emit('toggle-play');
    },

    playOnly() {
      // Just play without recording
      this.$emit('toggle-play');
    },

    recordAndPlay() {
      // Start recording and then play
      if (!this.isRecording) {
        this.startRecording();
      }
    },

    skipPrevious() {
      this.$emit('skip-previous');
    },

    skipNext() {
      this.$emit('skip-next');
    },

    toggleSettings() {
      this.showSettingsPanel = !this.showSettingsPanel;
    },

    // Start recording with countdown
    async startRecordingWithCountdown() {
      try {
        // First request permissions
        if (!await this.initializeRecording()) {
          return;
        }

        this.isPreparingRecording = true;
        this.recordingError = null;

        // Get display media (tab capture) with enhanced audio
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: this.recordingQuality.video,
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            sampleRate: { ideal: 48000 },
            channelCount: { ideal: 2 }
          },
          preferCurrentTab: true,
        });

        // Check if user selected current tab
        const videoTrack = displayStream.getVideoTracks()[0];
        const settings = videoTrack.getSettings();

        // Only proceed if a browser tab was selected
        if (settings.displaySurface !== 'browser') {
          this.recordingError = 'Please select a browser tab to record';
          displayStream.getTracks().forEach(track => track.stop());
          this.isPreparingRecording = false;
          return;
        }

        // Store the stream for later use
        this.pendingStream = displayStream;

        // Handle stream ended (user stopped sharing)
        displayStream.getVideoTracks()[0].onended = () => {
          this.cancelCountdown();
        };

        // Enter fullscreen first
        if (!this.isFullscreen) {
          await this.enterFullscreen();
        }

        // Wait a moment for fullscreen to settle
        await new Promise(resolve => setTimeout(resolve, 300));

        // Show countdown
        this.showCountdown = true;

        // Start countdown
        this.$nextTick(() => {
          if (this.$refs.countdown) {
            this.$refs.countdown.start();
          }
        });

      } catch (error) {
        console.error('Error preparing recording:', error);
        this.recordingError = error.message || 'Failed to start recording';
        this.isPreparingRecording = false;
        this.cleanup();
      }
    },

    // Handle countdown complete
    async onCountdownComplete() {
      this.showCountdown = false;

      // Give time for countdown to disappear and DOM to update
      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 300));

      if (!this.pendingStream) {
        this.recordingError = 'Recording stream not available';
        this.isPreparingRecording = false;
        return;
      }

      try {
        // Set up MediaRecorder with the stream
        this.recordingStream = this.pendingStream;
        this.pendingStream = null;

        this.mediaRecorder = new MediaRecorder(this.recordingStream, this.recordingOptions);
        this.recordedChunks = [];

        // Handle data available
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        // Handle recording stopped
        this.mediaRecorder.onstop = () => {
          this.saveRecording();
          this.cleanup();
        };

        // Handle errors
        this.mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event);
          this.recordingError = 'Recording error occurred';
          this.stopRecording();
        };

        // Start recording
        this.mediaRecorder.start(1000);
        this.isRecording = true;
        this.recordingStartTime = Date.now();
        this.startRecordingTimer();
        this.isPreparingRecording = false;

        // Start playback
        if (!this.isPlaying) {
          this.togglePlay();
        }

        // Hide all UI controls for clean recording
        this.showControls = false;
        this.forceHideAllUI = true;

      } catch (error) {
        console.error('Error starting recording:', error);
        this.recordingError = error.message || 'Failed to start recording';
        this.isPreparingRecording = false;
        this.cleanup();
      }
    },

    // Handle countdown cancelled
    onCountdownCancelled() {
      this.showCountdown = false;
      this.isPreparingRecording = false;

      // Stop the pending stream
      if (this.pendingStream) {
        this.pendingStream.getTracks().forEach(track => track.stop());
        this.pendingStream = null;
      }

      // Exit fullscreen if we entered it
      if (this.isFullscreen) {
        this.exitFullscreen();
      }

      // Show controls again
      this.showControls = true;
    },

    // Cancel countdown (from external trigger)
    cancelCountdown() {
      if (this.$refs.countdown) {
        this.$refs.countdown.cancelCountdown();
      }
    },

    // Show controls temporarily
    showControlsTemporarily() {
       // If in fullscreen and playing, never show controls
      if (this.isFullscreen && this.isPlaying) {
        this.showControls = false;
        return; // Exit early
      }

      // Clear existing timer
      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
      }

      // Show controls
      this.showControls = true;

      // Hide after delay if playing in recording mode and not recording
      if (this.isPlaying && this.recordingMode && !this.isRecording) {
        this.controlsTimer = setTimeout(() => {
          this.showControls = false;
        }, 2500);
      }
    },

    // Recording pause toggle
    toggleRecordingPause() {
      if (this.mediaRecorder) {
        if (this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.pause();
          this.isRecordingPaused = true;
        } else if (this.mediaRecorder.state === 'paused') {
          this.mediaRecorder.resume();
          this.isRecordingPaused = false;
        }
      }
    },

    // ---- Rest of the existing methods ----
    changeRandomAnimation() {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * this.animations.length);
      } while (newIndex === this.animationIndex && this.animations.length > 1);

      this.animationIndex = newIndex;

      const animContainer = this.$el.querySelector('.image-animation-container');
      if (animContainer) {
        animContainer.classList.add('animation-transition');

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        this.baseAnimation = this.animations[this.animationIndex];  // Use baseAnimation
        animContainer.classList.add(this.currentAnimation);  // This uses computed property

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    restartAnimations() {
      const animContainer = this.$el.querySelector('.image-animation-container');
      if (animContainer) {

        // Check if animations should be disabled
        if (this.disableAnimation || (this.animateOnlyWhenPlaying && !this.isPlaying)) {
          // Remove all animation classes
          this.animations.forEach(anim => {
            animContainer.classList.remove(anim);
          });
          animContainer.style.animationDuration = '';
          return;
        }

        if (this.segmentInProgress) {
          animContainer.classList.add('animation-transition');
        }

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        animContainer.classList.add(this.currentAnimation);

        if (this.holdAnimationAtEnd || this.synchronizeWithSpeaking) {
          const wordCount = this.activeSegmentContent.split(/\s+/).length;
          const estimatedSeconds = Math.max(10, Math.min(60, (wordCount / 90) * 60));
          animContainer.style.animationDuration = `${estimatedSeconds}s`;
          this.animationDuration = estimatedSeconds;
        } else {
          animContainer.style.animationDuration = '';
        }

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    startAnimationCycle() {
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }

      // Don't start animation cycle if disabled or if set to play-only and not playing
      if (this.disableAnimation || (this.animateOnlyWhenPlaying && !this.isPlaying)) {
        return;
      }

      if (this.animationStyle === 'random' && !this.holdAnimationAtEnd && !this.synchronizeWithSpeaking) {
        this.baseAnimation = this.animations[this.animationIndex]; // Use baseAnimation

        if (!this.noRepeatAnimation) {
          this.animationTimer = setInterval(() => {
            if (!this.segmentInProgress || !this.synchronizeWithSpeaking) {
              this.changeRandomAnimation();
            }
          }, 15000);
        }
      } else {
        const animMap = {
          'zoom-pan': 'zoom-pan-effect',
          'ken-burns': 'ken-burns-effect',
          'fade-scale': 'fade-and-scale',
          'gentle-float': 'gentle-float',
          'smooth-slide': 'smooth-slide',
          'hollywood-dramatic': 'hollywood-dramatic',
          'epic-reveal': 'epic-reveal',
          'dramatic-focus': 'dramatic-focus',
          'aerial-sweep': 'aerial-sweep',
          'directors-cut': 'directors-cut',
          'epic-pan': 'epic-pan',
          'dolly-zoom': 'dolly-zoom',
        };
        this.baseAnimation = animMap[this.animationStyle] || 'zoom-pan-effect';
      }

      this.$nextTick(() => {
        this.restartAnimations();
      });
    },

    // ---- File Type Detection Methods ----
    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isVideoFile(fileType) {
      return fileType && (
        fileType.startsWith('video/') ||
        fileType.includes('mp4') ||
        fileType.includes('mov') ||
        fileType.includes('quicktime') ||
        fileType.includes('webm') ||
        fileType.includes('ogg')
      );
    },

    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';
      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      if (this.isVideoFile(fileType)) return 'video-icon';
      return 'generic-icon';
    },

    handleMediaError(e) {
      console.warn('Media load error:', e.target.alt);
      e.target.style.display = 'none';

      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);

      const errorMsg = document.createElement('div');
      errorMsg.className = 'media-error-message';
      errorMsg.textContent = `${e.target.tagName === 'VIDEO' ? 'Video' : 'Image'} could not be loaded`;
      e.target.parentNode.appendChild(errorMsg);
    },

    // ---- Settings Methods ----
    setAnimationStyle(style) {
      this.animationStyle = style;
      this.startAnimationCycle();
      this.saveSettings();

      if (this.isPlaying) {
        this.restartAnimations();
      }
    },

    setAspectRatio(ratio) {
      this.aspectRatio = ratio;
      this.saveSettings();
    },

    // Update transcript background based on opacity
    updateTranscriptBackground() {
      // Create new background color with updated opacity
      const rgba = `rgba(0, 0, 0, ${this.transcriptOpacity})`;
      this.$emit('update:transcriptBackgroundColor', rgba);
      this.saveSettings();
    },

    // ---- Fullscreen Methods ----
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }

      // If in fullscreen and playing, hide controls
      if (this.isFullscreen && this.isPlaying) {
        this.showControls = false;
        this.forceHideAllUI = true;
      } else {
        // Otherwise show controls temporarily
        this.showControlsTemporarily();
      }
    },

    enterFullscreen() {
      const element = this.$el;

      try {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }

        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');

        // Lock orientation to landscape if possible
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(e => {
            console.log('Orientation lock not supported');
          });
        }

        // Add event listeners for resizing
        window.addEventListener('resize', this.handleFullscreenResize);
        window.addEventListener('orientationchange', this.handleFullscreenResize);

        // Force immediate resize handling
        this.handleFullscreenResize();
      } catch (e) {
        console.error('Fullscreen API error:', e);
        // Fallback for devices without proper fullscreen support
        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');
      }
    },

    exitFullscreen() {
      try {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }

        // Unlock orientation
        if (screen.orientation && screen.orientation.unlock) {
          screen.orientation.unlock();
        }

        // Remove event listeners
        window.removeEventListener('resize', this.handleFullscreenResize);
        window.removeEventListener('orientationchange', this.handleFullscreenResize);

        document.body.classList.remove('has-fullscreen-element');

        // Clean up styles
        this.$el.style.transform = '';
        this.$el.style.position = '';
        this.$el.style.top = '';
        this.$el.style.marginTop = '';

        // Emit event
        this.$emit('fullscreen-exited');
      } catch (e) {
        console.error('Exit fullscreen error:', e);
      }

      this.isFullscreen = false;
    },

    handleFullscreenResize() {
      if (this.isFullscreen) {
        this.$nextTick(() => {
          const element = this.$el;
          if (element) {
            // Force reflow for mobile browsers
            element.style.display = 'none';
            const forceRepaint = element.offsetHeight;
            element.style.display = '';

            setTimeout(() => {
              // Force hardware acceleration
              element.style.transform = 'translateZ(0)';

              // Ensure content fills the screen
              const viewportHeight = window.innerHeight;
              if (element.offsetHeight < viewportHeight) {
                element.style.height = `${viewportHeight}px`;
              }
            }, 50);
          }
        });
      }
    },

    handleFullscreenChange() {
      const isInFullscreen = !!(document.fullscreenElement ||
                                document.webkitFullscreenElement ||
                                document.msFullscreenElement);

      this.isFullscreen = isInFullscreen;

      if (isInFullscreen) {
        document.body.classList.add('has-fullscreen-element');
      } else {
        document.body.classList.remove('has-fullscreen-element');
      }
    },

    // ---- Settings Storage Methods ----
    saveSettings() {
      localStorage.setItem(`recording_settings_${this.messageId}`, JSON.stringify({
        aspectRatio: this.aspectRatio,
        animationStyle: this.animationStyle,
        noRepeatAnimation: this.noRepeatAnimation,
        holdAnimationAtEnd: this.holdAnimationAtEnd,
        synchronizeWithSpeaking: this.synchronizeWithSpeaking,
        recordingMode: this.recordingMode,
        transcriptOpacity: this.transcriptOpacity,
        podcastBackgroundColor: this.podcastBackgroundColor,
        animateOnlyWhenPlaying: this.animateOnlyWhenPlaying,
        disableAnimation: this.disableAnimation,
      }));
    },

    loadSettings() {
      const savedSettings = localStorage.getItem(`recording_settings_${this.messageId}`);
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          if (settings.aspectRatio) {
            this.aspectRatio = settings.aspectRatio;
          }
          if (settings.animationStyle) {
            this.animationStyle = settings.animationStyle;
          }
          if (settings.noRepeatAnimation !== undefined) {
            this.noRepeatAnimation = settings.noRepeatAnimation;
          }
          if (settings.holdAnimationAtEnd !== undefined) {
            this.holdAnimationAtEnd = settings.holdAnimationAtEnd;
          }
          if (settings.synchronizeWithSpeaking !== undefined) {
            this.synchronizeWithSpeaking = settings.synchronizeWithSpeaking;
          }
          if (settings.recordingMode !== undefined) {
            this.recordingMode = settings.recordingMode;
          }
          if (settings.transcriptOpacity !== undefined) {
            this.transcriptOpacity = settings.transcriptOpacity;
          }
          if (settings.podcastBackgroundColor !== undefined) {
            this.podcastBackgroundColor = settings.podcastBackgroundColor;
          }
          if (settings.animateOnlyWhenPlaying !== undefined) {
            this.animateOnlyWhenPlaying = settings.animateOnlyWhenPlaying;
          }
          if (settings.disableAnimation !== undefined) {
            this.disableAnimation = settings.disableAnimation;
          }
        } catch (e) {
          console.error('Error loading saved settings:', e);
        }
      }
    }
  }
};

</script>
<style scoped>
/* Recording toast notification - outside the recording area */
.recording-toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10001;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.recording-toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.recording-dot {
  width: 8px;
  height: 8px;
  background: #dc2626;
  border-radius: 50%;
  animation: recording-pulse 1.5s ease-in-out infinite;
}

@keyframes recording-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.3);
  }
}

/* Recording-specific styles */
.screen-recording-display.is-recording .ui-controls-layer {
  pointer-events: none;
}

.screen-recording-display.is-recording .ui-controls-layer.recording-hidden {
  opacity: 0;
  visibility: hidden;
}

/* Force hide all UI during recording */
.screen-recording-display.is-recording .ui-controls-layer.hidden {
  opacity: 0 !important;
  visibility: hidden !important;
}

.screen-recording-display.is-recording .ui-controls-layer.hidden * {
  opacity: 0 !important;
  visibility: hidden !important;
  display: none !important;
}

/* Ensure transcript remains visible during recording */
.screen-recording-display.is-recording .live-transcript {
  visibility: visible !important;
  opacity: 1 !important;
}

/* Previous styles from the original component */
.screen-recording-display {
  width: 100%;
  position: relative;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  outline: none; /* Remove outline when focused */
}

/* Aspect ratio classes */
.aspect-16-9 {
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
}

.aspect-4-3 {
  padding-top: 75%; /* 4:3 Aspect Ratio */
}

.aspect-1-1 {
  padding-top: 100%; /* 1:1 Square Aspect Ratio */
}

.aspect-9-16 {
  padding-top: 177.78%; /* 9:16 Vertical Aspect Ratio */
}

/* Fullscreen mode */
.screen-recording-display.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding-top: 0 !important;
  border-radius: 0;
  z-index: 9999;
  background-color: black;
  transition: none; /* Remove transitions in fullscreen for clean recording */
}

/* Document containers */
.documents-stack {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.document-item {
  position: relative;
  width: 100%;
  height: 100%;
}

.document-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.document-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Media container */
.media-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.media-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Image and video elements */
.document-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.document-video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* UI Controls Layer - main container for UI elements */
.ui-controls-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none; /* Let clicks pass through by default */
  transition: opacity 0.5s ease;
}

/* Hide controls in recording mode while playing */
.ui-controls-layer.hidden {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease, visibility 0s 0.5s;
}

/* Make buttons clickable */
.ui-controls-layer button {
  pointer-events: auto;
}

/* Minimalist fullscreen toggle optimized for recording */
.fullscreen-toggle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  opacity: 0.7;
}

.fullscreen-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
  opacity: 1;
}

/* In recording mode, make the fullscreen toggle more discreet */
.fullscreen-toggle.recording-mode {
  width: 36px;
  height: 36px;
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.5);
}

/* Settings toggle */
.settings-toggle {
  position: absolute;
  top: -25px;
  left: -5px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.settings-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
}

/* Settings panel */
.recording-settings {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 15;
}

.settings-panel {
  background: rgba(28, 28, 28, 0.85);
  border-radius: 8px;
  padding: 16px;
  width: 320px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: auto;

  /* Add scroll capability */
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

/* Webkit scrollbar styling for Chrome/Safari */
.settings-panel::-webkit-scrollbar {
  width: 6px;
}

.settings-panel::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.settings-panel::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.settings-panel::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .settings-panel {
    width: 280px;
    max-height: 60vh;
    padding: 12px;
  }
}

/* Very small screens */
@media (max-width: 480px) {
  .settings-panel {
    width: calc(100vw - 40px);
    max-height: 50vh;
  }
}

.settings-section {
  margin-bottom: 16px;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.settings-hint {
  opacity: 0.7;
  font-size: 0.85em;
  font-weight: normal;
  margin-left: 4px;
}

.setting-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.setting-button {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 6px 10px;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.setting-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.setting-button.active {
  background: rgba(66, 153, 225, 0.6);
}

/* Animation options */
.animation-options {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.settings-checkbox input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

/* Color and opacity controls */
.color-opacity-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.color-opacity-controls label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.color-opacity-controls input[type="range"] {
  flex: 1;
}

/* Large play button */
.large-play-button-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
}

.large-play-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
  margin: 0 1rem;
}

.large-play-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

.large-play-button svg {
  margin-left: 4px;
}

.nav-button {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.nav-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

/* Keyboard shortcuts overlay */
.keyboard-shortcuts-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  pointer-events: auto;
}

.keyboard-shortcuts-panel {
  background: rgba(40, 40, 40, 0.95);
  border-radius: 8px;
  padding: 24px;
  width: 360px;
  max-width: 90vw;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.keyboard-shortcuts-panel h3 {
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 18px;
}

.shortcut-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px 16px;
  margin-bottom: 20px;
}

.shortcut-item {
  display: contents;
}

.key {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 4px 8px;
  min-width: 24px;
  text-align: center;
  font-family: monospace;
  font-weight: bold;
}

.close-button {
  display: block;
  margin: 0 auto;
  background: rgba(66, 153, 225, 0.6);
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background: rgba(66, 153, 225, 0.8);
}

/* Image animation styles */
.image-animation-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Animation transitions */
.animation-transition {
  transition: transform 1s ease-in-out, opacity 1s ease-in-out;
}

/* Non-repeating animations */
.image-animation-container.no-repeat,
.holdAnimationAtEnd .image-animation-container,
.synchronizeWithSpeaking .image-animation-container {
  animation-iteration-count: 1 !important;
  animation-fill-mode: forwards !important;
}

/* Animation keyframes */
.zoom-pan-effect {
  animation: zoomPan 15s ease-in-out infinite;
}

.hollywood-dramatic {
  animation: hollywoodDramatic 30s ease-in-out infinite;
}

.epic-reveal {
  animation: epicReveal 25s ease-in-out infinite;
}
.dramatic-focus {
  animation: dramaticFocus 3s ease-in-out infinite;
}
.aerial-sweep {
  animation: aerialSweep 22s ease-in-out infinite;
}
.directors-cut {
  animation: directorsCut 28s ease-in-out infinite;
}
.epic-pan {
  animation: epicPan 24s ease-in-out infinite;
}
.dolly-zoom {
  animation: dollyZoom 26s ease-in-out infinite;
}
.ken-burns-effect {
  animation: kenBurns 20s ease-in-out infinite;
}

.fade-and-scale {
  animation: fadeScale 12s ease-in-out infinite;
}

.gentle-float {
  animation: gentleFloat 10s ease-in-out infinite;
}

.smooth-slide {
  animation: smoothSlide 18s ease-in-out infinite;
}

/* Modified keyframe animations with greater zoom magnitude */
@keyframes zoomPan {
  0% {
    transform: scale(1) translate(0, 0);
  }
  50%, 100% {
    transform: scale(1.2) translate(-3%, -2%); /* Increased from 1.1 to 1.2 */
  }
}

@keyframes kenBurns {
  0% {
    transform: scale(1.05) translate(-1%, -1%);
  }
  25% {
    transform: scale(1.12) translate(1%, 1%); /* Increased from 1.09 to 1.12 */
  }
  50%, 100% {
    transform: scale(1.2) translate(2%, 1%); /* Increased from 1.12 to 1.2 */
  }
}

@keyframes fadeScale {
  0% {
    opacity: 0.95;
    transform: scale(1);
  }
  50%, 100% {
    opacity: 1;
    transform: scale(1.12); /* Increased from 1.08 to 1.12 */
  }
}

@keyframes gentleFloat {
  0% {
    transform: translateY(0) scale(1.05); /* Added scale effect */
  }
  50%, 100% {
    transform: translateY(-12px) scale(1.15); /* Increased translation and added scale */
  }
}

@keyframes smoothSlide {
  0% {
    transform: translateX(-2%) scale(1.05);
  }
  50%, 100% {
    transform: translateX(3%) scale(1.15); /* Increased from 1.05 to 1.15 */
  }
}

@keyframes hollywoodDramatic {
  0% {
    transform: scale(1) translate(0, 0) rotate(0deg);
    filter: brightness(0.9) contrast(0.9);
  }
  15% {
    transform: scale(1.08) translate(-1%, -1%) rotate(-0.5deg);
    filter: brightness(0.95) contrast(1.05);
  }
  35% {
    transform: scale(1.15) translate(2%, -2%) rotate(0.3deg);
    filter: brightness(1.05) contrast(1.15);
  }
  65% {
    transform: scale(1.25) translate(-3%, -1%) rotate(-0.2deg);
    filter: brightness(1.1) contrast(1.2);
  }
  85%, 100% {
    transform: scale(1.35) translate(1%, -3%) rotate(0.1deg);
    filter: brightness(1.15) contrast(1.25);
  }
}
@keyframes epicReveal {
  0% {
    transform: scale(0.8) translate(0, 0);
    filter: brightness(0.4) contrast(0.9) saturate(0.8);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.15) translate(-2%, -4%);
    filter: brightness(1.1) contrast(1.35) saturate(1.15);
    opacity: 1;
  }
}

@keyframes dramaticFocus {
  0% {
    transform: scale(1.3) translate(-5%, -5%);
    filter: blur(8px) brightness(0.7) contrast(0.9);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.1) translate(3%, -1%);
    filter: blur(0px) brightness(1.1) contrast(1.25);
    opacity: 1;
  }
}

@keyframes aerialSweep {
  0% {
    transform: scale(1.1) translate(-8%, -3%) rotate(-1deg);
    filter: brightness(0.9) contrast(1.1) saturate(0.9);
  }
  100% {
    transform: scale(1.3) translate(8%, -3%) rotate(1deg);
    filter: brightness(1.1) contrast(1.3) saturate(1.2);
  }
}


@keyframes directorsCut {
  0% {
    transform: scale(1) translate(0, 0);
    filter: grayscale(0.3) brightness(0.85) contrast(1.1);
  }
  20% {
    transform: scale(1.08) translate(-2%, -1%);
    filter: grayscale(0.2) brightness(0.9) contrast(1.15) sepia(0.1);
  }
  40% {
    transform: scale(1.16) translate(-4%, -2%);
    filter: grayscale(0.1) brightness(0.95) contrast(1.2) sepia(0.15);
  }
  60% {
    transform: scale(1.24) translate(-2%, -3%);
    filter: grayscale(0) brightness(1) contrast(1.25) sepia(0.2);
  }
  80% {
    transform: scale(1.32) translate(0%, -2%);
    filter: grayscale(0) brightness(1.05) contrast(1.3) sepia(0.15);
  }
  100% {
    transform: scale(1.4) translate(2%, -1%);
    filter: grayscale(0) brightness(1.1) contrast(1.35) sepia(0.1);
  }
}

@keyframes epicPan {
  0% {
    transform: scale(1.1) translateX(-5%);
    filter: brightness(0.9) contrast(1.1);
  }
  100% {
    transform: scale(1.3) translateX(5%);
    filter: brightness(1.1) contrast(1.3);
  }
}

@keyframes dollyZoom {
  0% {
    transform: scale(1) perspective(500px) translateZ(0px);
    filter: brightness(0.9);
  }
  100% {
    transform: scale(1.3) perspective(500px) translateZ(50px);
    filter: brightness(1.1);
  }
}

/* File icon styles */
.file-icon {
  width: 120px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 2rem;
  color: white;
  position: relative;
  margin-bottom: 16px;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.video-icon:before { background: #E91E63; }
.generic-icon:before { background: #9E9E9E; }

.document-name {
  font-size: 1rem;
  text-align: center;
  max-width: 300px;
  word-break: break-word;
}

/* Placeholder styles */
.image-placeholder, .video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  opacity: 0.7;
}

.image-name, .video-name {
  font-weight: 500;
  text-align: center;
  margin-bottom: 0.5rem;
  max-width: 100%;
  word-break: break-word;
}

.image-info, .video-info {
  font-size: 0.875rem;
  color: #999;
  text-align: center;
}

.media-error-message {
  color: #ff6b6b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}

/* No documents placeholder */
.no-documents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #999;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Mobile optimization */
@media (max-width: 768px) {
  .settings-panel {
    width: 280px;
    padding: 12px;
  }

  .large-play-button {
    width: 60px;
    height: 60px;
  }

  .large-play-button svg {
    width: 36px;
    height: 36px;
  }

  .nav-button {
    width: 40px;
    height: 40px;
  }

  .file-icon {
    width: 80px;
    height: 100px;
    font-size: 1.5rem;
  }

  .fullscreen-toggle,
  .settings-toggle {
    width: 36px;
    height: 36px;
  }
}

/* iOS Safari fixes for fullscreen */
@supports (-webkit-touch-callout: none) {
  .screen-recording-display.is-fullscreen {
    /* iOS Safari specific fixes */
    top: env(safe-area-inset-top, 0);
    bottom: env(safe-area-inset-bottom, 0);
    left: env(safe-area-inset-left, 0);
    right: env(safe-area-inset-right, 0);
    height: -webkit-fill-available;
  }
}

/* Screen capture optimizations - prevent flickering in recordings */
.screen-recording-display.is-fullscreen {
  backface-visibility: hidden; /* Prevent flickering */
  transform: translateZ(0); /* Force hardware acceleration */
  will-change: transform; /* Hint to browser for optimization */
}

/* Ensure smooth animation playback in fullscreen */
.screen-recording-display.is-fullscreen .image-animation-container {
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform;
}

/* Prevent flickering in fullscreen */
.screen-recording-display.is-fullscreen .document-item {
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform;
}

/* Custom focus styles for accessibility */
.screen-recording-display:focus {
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
  outline: none;
}

/* Large record button - with better styling */
.large-record-button {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: 3px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
  transition: all 0.3s ease;
  margin: 0 1rem;
  position: relative;
  overflow: hidden;
}

.large-record-button:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%);
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.large-record-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.5);
}

.large-record-button:hover:before {
  opacity: 1;
}

.large-record-button svg {
  z-index: 1;
  width: 36px;
  height: 36px;
}

.large-record-button svg circle:last-child {
  fill: currentColor;
  animation: pulse-record 2s ease-in-out infinite;
}

@keyframes pulse-record {
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.recording-toast kbd {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  font-family: monospace;
  font-size: 13px;
  margin: 0 3px;
  display: inline-block;
  line-height: 1;
  vertical-align: middle;
}

.preparing-recording {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  z-index: 15;
}

.preparing-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  margin: 0 auto 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Add or modify this CSS rule */
.is-fullscreen.is-playing .ui-controls-layer {
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

/* Font Settings Section CSS - Add this to your ScreenRecordingDisplay component styles */

/* Font Settings Section */
.font-settings-section {
  margin-bottom: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 16px;
}

.customization-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #ffffff;
  font-weight: 500;
}

.customization-label svg {
  width: 16px;
  height: 16px;
  opacity: 0.8;
}

.font-control {
  margin-bottom: 12px;
}

.font-control:last-child {
  margin-bottom: 0;
}

.font-control label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.font-select {
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 6px 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  /* Ensure text color is white */
  -webkit-text-fill-color: white;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.font-select:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.font-select:focus {
  outline: none;
  border-color: rgba(66, 153, 225, 0.6);
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2);
  color: white;
}

.font-select option {
  background: #2d2d2d;
  color: white;
  padding: 4px 8px;
}

.range-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.font-range {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.font-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: rgba(66, 153, 225, 0.8);
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.font-range::-webkit-slider-thumb:hover {
  background: rgba(66, 153, 225, 1);
  transform: scale(1.1);
}

.font-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: rgba(66, 153, 225, 0.8);
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.font-range::-moz-range-thumb:hover {
  background: rgba(66, 153, 225, 1);
  transform: scale(1.1);
}

.range-value {
  min-width: 40px;
  text-align: right;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-family: monospace;
}

/* Font preset buttons styling */
.font-control .setting-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.font-control .setting-button {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.font-control .setting-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.font-control .setting-button.active {
  background: rgba(66, 153, 225, 0.6);
  box-shadow: 0 1px 3px rgba(66, 153, 225, 0.3);
}

/* Enhanced color picker for font settings */
.font-control .color-picker-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.font-control .current-color {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.font-control .color-picker {
  width: 28px;
  height: 28px;
  -webkit-appearance: none;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
  flex-shrink: 0;
}

.font-control .color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.font-control .color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}

.font-control .reset-button {
  font-size: 11px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.font-control .reset-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

</style>
